import { fetchAuthSession, signOut } from "aws-amplify/auth";
import { BASE_API_URL } from "../constants/apiEndpoints";
import { logger } from "../utils/logger";
import { recordRumError } from "../utils/rum";

const authFetch = async (url, options = {}) => {
  //強制ログアウトの確認
  let session;
  try {
    session = await fetchAuthSession();
  } catch (error) {
    if (error.name === "NotAuthorizedException") {
      // セッション無効 → ログイン画面へ
      await signOut({ global: false });
      window.location.href = "/";
      return;
    }
    throw error;
  }

  const idToken = session.tokens?.idToken?.toString();
  let version = process.env.REACT_APP_VERSION;

  //ReactNativeへのメッセージ送信処理
  const send = (type) => {
    if (window.ReactNativeWebView?.postMessage) {
      window.ReactNativeWebView?.postMessage(JSON.stringify({ type }));
    }
  };
  //ネイティブアプリかどうかを判定
  const hasReactNativeWebView = () =>
    typeof window !== "undefined" &&
    !!window.ReactNativeWebView &&
    typeof window.ReactNativeWebView.postMessage === "function";
  //バージョン取得
  if (hasReactNativeWebView()) {
    version = window.APP_ENV?.appVersion;
  }

  return fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${idToken}`,
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(version && { "X-App-Version": version }),
      ...options.headers,
    },
  })
    .then(async (res) => {
      if (res.ok) {
        return res;
      }
      if (res.status === 400 || res.status === 401) {
        // 業務エラーは呼出元でハンドリングする
        return res;
      }
      if (res.status === 426) {
        // バージョンエラー
        await forcedUpdate();
        send("FORCE_UPDATE");
        return res;
      }

      throw new Error("HTTP Status=" + res.status);
    })
    .catch((e) => {
      logger.error("API Error:", e);
      // エラーを RUM に分類記録（失敗の握りつぶし）
      const isHttpError = e.message?.startsWith("HTTP Status=");
      const kind = e instanceof TypeError ? "network" : (isHttpError ? "http_5xx" : "other");
      const status = isHttpError ? parseInt(e.message.slice("HTTP Status=".length), 10) : null;
      recordRumError(e, { kind, status, url: typeof window !== "undefined" ? window.location.href : null });
      // ビーコン送信の取りこぼし防止のため遷移を次 tick に遅延
      setTimeout(() => {
        location.href = "/Error";
      }, 0);
      const error = new Error("APIリクエストに失敗しました。");
      throw error;
    });
};

export const getFetch = (url) =>
  authFetch(url, {
    method: "GET",
  });

export const postFetch = (url, body) =>
  authFetch(url, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const postFetchGzip = (url, body) => {
  const stream = new Blob([JSON.stringify(body)])
    .stream()
    .pipeThrough(new CompressionStream("gzip"));
  return authFetch(url, {
    method: "POST",
    headers: {
      "Content-Encoding": "gzip",
      "Content-Type": "application/json",
    },
    duplex: "half",
    body: stream,
  });
};

export const putFetch = (url, body) =>
  authFetch(url, {
    method: "PUT",
    body: JSON.stringify(body),
  });

/*
課題・障害番号: KOSHO-434
対応日: 2026/04/20
担当者: 小林
修正概要: 削除履歴の一覧から企業を選択削除するとAPIエラーが発生する問題を対応
*/
export const deleteFetch = (url, body) =>
  authFetch(url, {
    method: "DELETE",
    body: JSON.stringify(body),
  });
/*
END
課題・障害番号: KOSHO-434
*/

export const apiErrorHandller = (res, data, callback) => {
  if (res.ok) {
    return callback(data);
  } else {
    const errorBody = data;
    const message = `${errorBody.message}APIリクエスト失敗。status：${res.status}`;
    throw new Error(message);
  }
};

export const fetchDataAsync = async (
  args = { method: "GET", path: "", data: undefined, headers: undefined },
  apiErrorCallback = apiErrorHandller
) => await fetchData(args, (data) => data, apiErrorCallback);

// 指摘 HIGH No.18: 機能していない default 引数を削除。
// 実際の呼び出し元は全て args を渡しているため default が適用されることはなく、
// 読み手の誤解を招く要因になっていた。X-App-Version の付与は authFetch 内で行われる。
export const fetchData = async (
  args,
  callback = (data) => data,
  apiErrorCallback = apiErrorHandller
) => {
  const { method, path, data, ...options } = args;
  const baseUrl = BASE_API_URL;
  const url =
    method !== "GET"
      ? `${baseUrl}${path}`
      : `${baseUrl}/${path}?${new URLSearchParams(data).toString()}`;
  try {
    return await authFetch(url, {
      method: method,
      ...options,
      body: method !== "GET" && data ? JSON.stringify(data) : undefined,
    })
      .then(async (res) => ({ res, data: await res.json() }))
      .catch((e) => {
        logger.error("API Error:", e);
        recordRumError(e, { kind: "api_fetch", url: url });
        const error = new Error("APIリクエストに失敗しました。");
        throw error;
      })
      .then((result) => {
        const { res, data } = result;
        return apiErrorCallback(res, data, callback);
      });
  } catch (e) {
    if (e instanceof Error) {
      logger.error("API Error:", e);
      recordRumError(e, { kind: "api_catch", url: url });
      const error = new Error("APIリクエストに失敗しました。");
      throw error;
    } else {
      throw e;
    }
  }
};

export const fetchGet = async (
  path,
  callback = (data) => data,
  data = {},
  options = {},
  apiErrorCallback = apiErrorHandller
) => {
  await fetchData({ method: "GET", path, data, ...options }, callback, apiErrorCallback);
};

export const fetchPost = async (
  path,
  callback = (data) => data,
  data = {},
  options = {},
  apiErrorCallback = apiErrorHandller
) => {
  await fetchData({ method: "POST", path, data, ...options }, callback, apiErrorCallback);
};

export const fetchPut = async (
  path,
  callback = (data) => data,
  data = {},
  options = {},
  apiErrorCallback = apiErrorHandller
) => {
  await fetchData({ method: "PUT", path, data, ...options }, callback, apiErrorCallback);
};

export const fetchGetAsync = async (
  path,
  data = {},
  options = {},
  apiErrorCallback = apiErrorHandller
) => await fetchDataAsync({ method: "GET", path, data, ...options }, apiErrorCallback);

export const fetchPostAsync = async (
  path,
  data = {},
  options = {},
  apiErrorCallback = apiErrorHandller
) => await fetchDataAsync({ method: "POST", path, data, ...options }, apiErrorCallback);

export const fetchPutAsync = async (
  path,
  data = {},
  options = {},
  apiErrorCallback = apiErrorHandller
) => await fetchDataAsync({ method: "PUT", path, data, ...options }, apiErrorCallback);

/*
課題・障害番号: 強制ログアウト対応
対応日: 2026/05/13
担当者: 影山
修正概要: 強制アップデート時に強制ログアウトさせるよう修正。
         指摘 HIGH No.19 で課題番号コメントブロック規約への準拠を追加。
*/
const forcedUpdate = async () => {
  const div = document.createElement("div");
  const limit = 5000;
  const ms = 1000;
  try {
    // Cognitoセッションクリア
    await signOut({ global: false });
    // 旧セッション情報の残存防止: localStorage を明示的にクリアする
    localStorage.clear();
    // 簡易モーダル生成
    div.innerHTML = `
    <div style="
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: #FFFFFF;
      border: 1px solid #999999;
      padding: 16px 24px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, .2);
      z-index: 9999;
    ">
      システム更新のため${limit / ms}秒後にログイン画面に遷移します。
    </div>
    `;
    document.body.appendChild(div);
  } catch (e) {
    logger.error("Forced update error:", e);
  } finally {
    setTimeout(() => {
      div.remove();
      // キャッシュ破棄した上でリロード
      window.location.replace(`/?reload=${Date.now()}`);
    }, limit);
  }
};
/*
END
課題・障害番号: 強制ログアウト対応
*/
